/// <reference path="../definitions/QueryTs.d.ts" />
/// <reference path="../definitions/QueryTs.Template.d.ts" />
/// <reference path="../definitions/QueryTs.SVG.d.ts" />
/// <reference path="../definitions/QueryTs.Touch.d.ts" />
/// <reference path="../definitions/QueryTs.Upload.d.ts" />

type Languages = 'nl' | 'de' | 'en';

type Icons = 'warning' | 'error' | 'info' | 'check' | 'clear' | 
    'first_page' | 'last_page' |
    'chevron_left' | 'chevron_right' | 
    'backspace' | 'close' | 'send' | 'save' |
    'apps' | 'view_comfy' | 'view_stream' | 'view_column' | 'compare' | 'cached' |
    'arrow_back' | 'arrow_forward' | 'filter_list' | 'select_all' |
    'add' | 'edit' | 'delete' | 'done' |
    'keyboard_tab' | 'keyboard_return' | 'keyboard_capslock' | 'get_app' |
    'more_vert' | 'swap_horiz' | 'rotate_right' |
    'my_location';

type UserSettings = 'UseHelpers' | 'CalculatorUsePicker' | 'NoAutoPopout' | 'NoAutoTab' | 'NoNavigation' | 'UseAlternativeSalesUnitDisplay' | 'HideHtmlView';
type ControlMode = 'Read' | 'Write' | 'Hide';

type SiteResponseTypes = 'Message' | 'Card' | 'CardAction' | 'Modal' | 'Redirect' | 'Validation' | 'Multi';
type SiteResponseCardSizes = 'Normal' | 'Normal2' | 'Normal3' | 'Small' | 'Large';
type SiteResponseCardObserverSorts = 'Event' | 'Change';
type SiteResponseCardObserverEvents = 'Scan' | 'Photo';
type SiteTelephoneNumberSort = 'CallTo' | 'Tel' | 'Skype' | 'Dial' | 'FaceTime';

type LanguageValues = { [k in Languages]?: string | null }
type LanguageNames = { [k in Languages]?: string }

class SiteColorScheme {
    red: string = "";
    orange: string = "";
    green: string = "";
    blue: string = "";
}

class SiteConfig {
    language: Languages = "nl";
    languageNames: LanguageNames = {};
    username: string = "";
    version: string = "";
    customer: string = "";
    settings: UserSettings[] = [];
    salesUnitOrder: SalesUnits[] = [];
    strings: { [k: string]: string } = {};
    colorScheme: SiteColorScheme = new SiteColorScheme();
    telephoneNumberSort: SiteTelephoneNumberSort | null = null;
    startPage: IActionInfo | null = null;
    startPageName: string = "";
    barcodeProfile: BarcodeProfiles = "Profile1";

    _languageEvent: QueryEventAsync<Languages>;
    get languageEvent(){ return this._languageEvent.expose(); } 

    constructor() {
        this._languageEvent = new QueryEventAsync<Languages>();
    }

    setLanguage(language: Languages) {
        let oldLanguage = this.language;
        this.language = language;
        this._languageEvent.trigger(oldLanguage);
    }
}
interface IMenuConfig {
    groups: ISiteStartSection[];
}

interface ISiteResponse {
    type: SiteResponseTypes;
}
interface ISiteResponseRedirectAction {
    actionInfo: IActionInfo;
    model: any;
    type: string;
}
interface ISiteResponseRedirect
    extends ISiteResponse {

    action: ISiteResponseRedirectAction;

}
interface ISiteResponseCardMetaData {
    info: any;
    controlMode: ControlMode;
}
interface ISiteResponseCardAction
    extends ISiteResponse {
    
    action: IActionType;
}
interface ISiteResponseMultiAction
    extends ISiteResponse {
    
    actions: ISiteResponse[];
}
interface ISiteResponseCard 
    extends ISiteResponse {
    
    options: {
        title?: string;
        size?: SiteResponseCardSizes;
        cardItems?: ISiteCardMenuItem[];
        backAction?: IActionType;
    }
    observers: ISiteResponseCardObserver[];
    view: string;
    model: any;
    metaData: any;
}

interface ISiteResponseCardObserver {
    sort: SiteResponseCardObserverSorts;
}
interface ISiteResponseCardObserverEvent
    extends ISiteResponseCardObserver {
    path: string;
    event: SiteResponseCardObserverEvents;
    action: IActionTypeRequest;
}
interface ISiteResponseCardObserverChange
    extends ISiteResponseCardObserver {
    type: string;
}

interface ISiteValidation {
    property: { [property: string]: string[] };
    message: string;
}
interface ISiteResponseValidation
    extends ISiteResponse, ISiteValidation {
}
interface ISiteResponseModal 
    extends ISiteResponse {
    
    modal: ISiteModal;
}
interface ISiteResponseMessage
    extends ISiteResponse, ISiteMessage {
    
    affectedTypes: string[];
}

class Site 
    extends QueryElement<HTMLElement> {
    
    private readonly _config: SiteConfig;
    private readonly _style: SiteStyle;
    private readonly _messages: SiteMessages;
    private readonly _modal: SiteModal;
    private readonly _localService: LocalService;
    private readonly _theme: IQueryElement;

    private readonly _start: SiteStart;
    private readonly _history: SiteHistory;
    private readonly _containerSide: SiteContainerSide;
    private readonly _sounds: SiteSounds;

    private readonly _overview: SiteOverview;
    private readonly __containers: IQueryElement;

    private _navigation?: SiteNavigation;

    private readonly _observer = new QueryEventAsync<IMessage>();
    private readonly _keyboard: SiteKeyboard;
    public get observer() { return this._observer.exposeTrigger(); };
    
    static mobileWidth: number = 605;
    static isMobile() {
        let body = QueryElement.get(document.body).position();
        if(body === null)
            return true;
        
        return body.width <= Site.mobileWidth;
    }
    static hasKeyboard() {
        return navigator.userAgent.match(/MC32/i);
    }
    static getSite() {
        let content = document.getElementById("content");
        if (!content)
            throw "Couldn't find Site element";
        
        return QueryElement.get<Site>(content);
    }

    get Config() { return this._config; }

    constructor(element: HTMLElement, siteConfig: SiteConfig, menuConfig: IMenuConfig) {
        super(element);
        
        let head = new Query('head').first();
        let theme = new Query('#theme').first();
        let body = this.parent();
        let containers = this.query('#containers').first();

        let modal = this.query('#modal').create<SiteModal, HTMLElement>(a => new SiteModal(a));
        let messages = this.query('#messages').create<SiteMessages, HTMLElement>(a => new SiteMessages(a));
        let start = this.query('#start').create<SiteStart, HTMLElement>(a => new SiteStart(a, this, menuConfig.groups));
        let overview = this.query('#overview').create<SiteOverview, HTMLElement>(a => new SiteOverview(a, this));
        let sounds = body && body.query("#sounds").create<SiteSounds, HTMLElement>(a => new SiteSounds(a));

        let containerSide = body && body.query('#container-side').create<SiteContainerSide, HTMLElement>(a => new SiteContainerSide(a, this));
        this.query('#version').all(a => a.setHtml(`${siteConfig.version} - ${siteConfig.username}`));

        if (head === null || containers === null || modal === null 
            || messages === null || start === null || overview === null
            || containerSide === null || theme === null || sounds === null)
            throw "Default element(s) not found!";

        this._config = siteConfig;
        this.__containers = containers;
        this._style = new SiteStyle(head);
        this._localService = new LocalService(this.observer);
        this._history = new SiteHistory();
        this._containerSide = containerSide;
        this._theme = theme;

        this._modal = modal;
        this._messages = messages;
        this._start = start;
        this._overview = overview;
        this._sounds = sounds;
        this._keyboard = new SiteKeyboard(this, siteConfig.barcodeProfile);
        
        // Set title
        document.title = this._config.customer + " :: EasyFlor";
        
        // Add css
        let cssLines = [
            ".state-color-Green { background: " + siteConfig.colorScheme.green + " }",
            ".state-color-Orange { background: " + siteConfig.colorScheme.orange + " }",
            ".state-color-Red { background: " + siteConfig.colorScheme.red + "}",
            ".state-color-Blue { background: " + siteConfig.colorScheme.blue + " }",
            ".state-color-RedBlue { background: linear-gradient(to bottom, " + siteConfig.colorScheme.red + " 49%, #FFFFFF 50%, " + siteConfig.colorScheme.blue + " 51%) }",
            ".state-color-GreenBlue { background: linear-gradient(to bottom, " + siteConfig.colorScheme.green + " 49%, #FFFFFF 50%, " + siteConfig.colorScheme.blue + " 51%) }",
            ".state-color-OrangeBlue { background: linear-gradient(to bottom, " + siteConfig.colorScheme.orange + " 49%, #FFFFFF 50%, " + siteConfig.colorScheme.blue + " 51%) }",
            ".state-color-GreenRed { background: linear-gradient(to bottom, " + siteConfig.colorScheme.green + " 49%, #FFFFFF 50%, " + siteConfig.colorScheme.red + " 51%) }",
            ".state-color-GreenOrange { background: linear-gradient(to bottom, " + siteConfig.colorScheme.green + " 49%, #FFFFFF 50%, " + siteConfig.colorScheme.orange + " 51%) }",
            ".state-color-OrangeRed { background: linear-gradient(to bottom, " + siteConfig.colorScheme.orange + " 49%, #FFFFFF 50%, " + siteConfig.colorScheme.red + " 51%) }"
        ];
        for (let cssLine of cssLines)
        {
            this._style.addCss(cssLine);
        }
        // Set renderers
        if (this._config.settings.includes("UseAlternativeSalesUnitDisplay"))
            QueryTemplateRenderers.appendOrOverwrite('salesUnit', new QueryTemplateRendererSalesUnitAlternative());
        
        if (this._config.settings.includes("HideHtmlView"))
            this.showHideConfidential();

        this._history.historyEvent.on(async (a) => {
            this.back();
        });
        this.on("qts.functionKey", (e: CustomEvent) => {
            this.getActiveContainers()
                .all<SiteContainer, any>(a => a.trigger("qts.functionKey", false, e.detail));
        });
        this.on("qts.observer.event", (e: CustomEvent) => {
            this.getActiveContainers()
                .all<SiteContainer, any>(a => a.trigger("qts.observer.event", false, e.detail));
        });
        this.on("qts.observer.change", (e: CustomEvent) => {
            this.getContainers()
                .all<SiteContainer, any>(a => a.trigger("qts.observer.change", false, e.detail));
        });
        this._observer.on(async (a) => {
            this.trigger("qts.observer.event", false, a);
        });

        window.onresize = () => this.resize();
        this.resize();
    }
    showHideConfidential() {
        if (this.containsClass("confidential-mode")) {
            this.removeClass("confidential-mode");
        } else {
            this.addClass("confidential-mode");
        }
    }
    containerSide() {
        if (this.containsClass("container-side")) {
            this.removeClass("container-side");
            this._containerSide.close();
        } else {
            this.addClass("container-side");
            this._containerSide.open();
        }
        this.resize();
    }
    switchTheme() {
        var attr = this._theme.getAttr("href");
        if(attr && attr.endsWith("style-light.css"))
            this._theme.setAttr("href", "/css/style-dark.css");
        else
            this._theme.setAttr("href", "/css/style-light.css");
    }
    playSound(sound: string) {
        this._sounds.play(sound);
    }
    back() {
        let activeContainers = this.getActiveContainers().all<SiteContainer, SiteContainer>(a => a);
        
        for(let container of activeContainers)
        {
            if (container.hasFocus())
                return container.back();
        }

        let container = activeContainers.first();
        if (container != null) {
            return container.back();
        }
    }
    resize() {
        this.getActiveContainers()
            .all<SiteContainer, any>(container => container.resize());
        
        let navigator : typeof SiteNavigationMobile | typeof SiteNavigationDesktop | typeof SiteNavigationNone;
        if (this._config.settings.includes("NoNavigation"))
            navigator = SiteNavigationNone;
        else if (Site.isMobile())
            navigator = SiteNavigationMobile;
        else
            navigator = SiteNavigationDesktop;
        
        // If we have another positioner, destruct and create new one
        if (!(this._navigation instanceof navigator)) {
            // Get current state and destruct
            let isActive = false;
            if (this._navigation !== undefined) {
                isActive = this._navigation.isActive();
                this._navigation.remove();
            }
            
            // Construct new one:
            this._navigation = new navigator(
                this
            ).appendTo(this);
            
            // Set old state
            if (isActive)
                this._navigation.makeActive();    
        }
    }
    
    getModals() {
        return this._modal.getModals();
    }
    showModal(card: ISiteCard, modal: ISiteModal) {
        return this._modal.show(card, modal);
    }
    
    addMessage(message: SiteMessage) {
        this._messages.add(message);
        return this;
    }

    async overview(append: boolean) {
        if(!append)
            await Promise.all(this.getActiveContainers()
                .all<SiteContainer, Promise<void>>(async a => { 
                    await this.makeContainerInactive(a) 
                }
            ));
        
        this._overview.makeActive();

        this._start.makeActive();
        if(this._navigation)
            this._navigation.makeInactive();
    }
    toggleSplit() {
        this.__containers.toggleClass("split-rotate");

        // And resize:
        this.resize();
    }
    isSplit() {
        let split = 0;
        this.getActiveContainers()
            .all<SiteContainer, any>(a => split++);

        return split > 1;
    }
    getHistory() {
        return this._history;
    }
    
    /// -- Container manager
    async createContainer(item: ISiteStartSectionItem) {
        let container = new SiteContainer(this, item)
            .appendTo(this.__containers);

        // Get instance number and register color
        let instance = Number(container.getAttr("data-instance"));
        this._style.addColor(
            instance, 
            item.color || "#000");
        
        // Register:
        await this._overview.register(container);

        return container;
    }
    async post(request: ISiteRequest): Promise<boolean> {
        let printPage = window.open("", "_blank", undefined, false);

        if (printPage) {
            printPage.document.open();
            printPage.document.writeln("<style>");
            printPage.document.writeln("body{display:flex;min-height:100vh;justify-content:center;align-items: center;flex-direction: column;}span{line-height:40px;}");
            printPage.document.writeln("svg{width:100px;height:100px;animation: rotate 2s linear infinite;transform-origin:center center;}");
            printPage.document.writeln("svg circle{stroke-dasharray: 1, 200;stroke-dashoffset: 0;animation: dash 1.5s ease-in-out infinite;stroke-linecap: round;stroke: #000;}");
            printPage.document.writeln("@keyframes rotate{100%{transform:rotate(360deg);}}");
            printPage.document.writeln("@keyframes dash{0%{stroke-dasharray:1,200;stroke-dashoffset:0;}50%{stroke-dasharray: 89, 200;stroke-dashoffset: -35px;}100%{stroke-dasharray: 89, 200;stroke-dashoffset: -124px;}}");
            printPage.document.writeln("</style>");
            printPage.document.writeln("<body><svg viewBox=\"25 25 50 50\"><circle cx=\"50\" cy=\"50\" r=\"20\" fill=\"none\" stroke-width=\"6\" stroke-miterlimit=\"10\"></circle></svg><span>Bezig met ophalen...</span></body>");

            // Do the request
            let queryRequest = await new QueryRequest(
                SiteAction.toLink(request.request),
                request.model ?
                {
                    contentType: 'json',
                    data: request.model,
                    method: 'post'
                }
                : undefined
            ).request();
        
            printPage.document.close();
            printPage.document.open();

            let contentTypeInfo = queryRequest.headers.get("Content-Type") || "";
            let contentDisposition = queryRequest.headers.get('Content-Disposition') || "";
            let contentType = contentTypeInfo.split(";")[0];
            
            if (queryRequest.status == 200) {
                switch (contentType) {
                    default:
                        printPage.close();
                        break;
                    case "application/json":
                    case "text/html":
                        printPage.document.write(await queryRequest.text());
                        break;
                    case "application/download":
                    case "image/jpeg":
                            let filenameRegex = /filename=\"(.*?)\"|filename=([^\s;]*)/;
                            let filenameMatches = filenameRegex.exec(contentDisposition);
                            let a = document.createElement("a");
                            if (filenameMatches != null)
                                a.download = filenameMatches[1] || filenameMatches[2];
                            a.href = await QueryRequest.blobToBase64(await queryRequest.blob());
                            a.click();

                            printPage.close();
                        break;
                    case "application/pdf":
                        printPage.document.write("<body style='margin: 0; padding: 0; display: flex;min-height: 100vh;flex-direction: column'><object style='flex: 1' data='")
                        printPage.document.write(await QueryRequest.blobToBase64(await queryRequest.blob()));
                        printPage.document.write("'></object></body>");
                        break;
                }
            } else if (queryRequest.status == 404) {
                // 404 is no error, close the page
                printPage.close();
            }
            printPage.document.close();
        }
        
        return true;
    }

    async makeContainerActive(container: SiteContainer, append: boolean = true) {
        if(container.isActive())
            return;
        
        if(!append)
            await Promise.all(
                    this.getActiveContainers()
                        .all<SiteContainer, any>(a => this.makeContainerInactive(a))
                );
        
        this.trigger("qts.container", false, container);

        // And at last call the container
        await container.makeActive();

        // Make navigation active
        if(this._navigation && !this._navigation.isActive()) {
            await this._overview.makeItemActiveByContainer(container);
            this._navigation.makeActive();
        }

        // And start inactive
        this._start.makeInactive();
        this._style.setColor(container.Config.color || "#000");
    
        // Now all containers are active, call resize to adjust their size
        this.resize();

        return this;
    }
    async makeContainerInactive(container: SiteContainer) {
        await this._overview.makeItemInactiveByContainer(container);
        if(await container.makeInactive()) {

            if(this._navigation && !this._navigation.isActive()) {
                this._overview.makeActive();
            }
        }

        return this;
    }
    async removeContainer(container: SiteContainer) {
        await this._overview.removeItemByContainer(container);
        await container.remove();
        let otherActive = this.getActiveContainers().first();
        
        // TODO: MakeContainerActive should call 
        // CardActive to call a label to get focus
        if(otherActive !== null) 
            this.resize();
            //await this.makeContainerActive(otherActive);
        else 
            await this.overview(false);
    }
    private getContainers() {
        return this.query("#containers > .container");
    }
    private getActiveContainers() {
        return this.query("#containers > .container.active");
    }
}


function delay(ms: number) {
    return new Promise<void>(function(resolve) {
        setTimeout(resolve, ms);
    });
}

function throwExpression(errorMessage: string): never {
    throw new Error(errorMessage);
  }